import React from "react";
import { 
  Sparkles, Search, Activity, Compass, Bookmark, PhoneCall, Globe, 
  Kanban, MapPin, ArrowRight, ShieldCheck, Check
} from "lucide-react";
import { getAllFeatures, FeatureRecord } from "../../utils/featureRegistry";
import { useSeoMetadata } from "../PublicPages";
import { SeoBreadcrumbs } from "./SeoBreadcrumbs";
import { SeoRelatedLinks } from "./SeoRelatedLinks";

interface FeatureHubPageProps {
  onNavigate: (path: string) => void;
  onLogin?: (isSignUp?: boolean) => void;
  currentUser?: any;
}

export function FeatureHubPage({ onNavigate, onLogin = () => {}, currentUser }: FeatureHubPageProps) {
  const features = getAllFeatures();

  useSeoMetadata({
    title: "BizNearYou Features & Local Discovery Tools",
    description: "Explore BizNearYou features including Local Business Discovery, Hiring Signals, Opportunity Scores, Contact Search, Website Audits, and CRM Pipeline.",
    canonicalUrl: "https://getbiznearyou.com/features",
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "BizNearYou Local Employer Discovery Platform",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      }
    }
  });

  const getIcon = (slug: string) => {
    switch (slug) {
      case "local-business-discovery": return <Search className="w-5 h-5 text-indigo-600" />;
      case "hiring-signals": return <Activity className="w-5 h-5 text-emerald-600" />;
      case "opportunity-score": return <Compass className="w-5 h-5 text-amber-600" />;
      case "saved-businesses": return <Bookmark className="w-5 h-5 text-rose-600" />;
      case "contact-search": return <PhoneCall className="w-5 h-5 text-cyan-600" />;
      case "website-review": return <Globe className="w-5 h-5 text-blue-600" />;
      case "crm-pipeline": return <Kanban className="w-5 h-5 text-purple-600" />;
      case "route-planner": return <MapPin className="w-5 h-5 text-orange-600" />;
      default: return <Sparkles className="w-5 h-5 text-indigo-600" />;
    }
  };

  const handleLinkClick = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    onNavigate(path);
  };

  return (
    <div className="w-full">
      <main className="flex-grow max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SeoBreadcrumbs items={[{ label: "Features", path: "/features" }]} onNavigate={onNavigate} />

        {/* Hero Banner */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-10 mb-10 shadow-xs relative overflow-hidden">
          <div className="absolute top-0 right-0 -mt-10 -mr-10 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative z-10 max-w-3xl space-y-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-indigo-50 dark:bg-indigo-950/80 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Platform Capabilities Overview</span>
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold text-slate-950 dark:text-white tracking-tight leading-tight">
              Tools Built for Local Job Seekers & Career Switchers
            </h1>
            <p className="text-base sm:text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
              BizNearYou equips you with verified employer maps, commercial growth signals, opportunity scoring, direct contact lookups, and visual pipeline management.
            </p>
            <div className="pt-2">
              <a
                href="/find-jobs"
                onClick={(e) => handleLinkClick(e, "/find-jobs")}
                className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm transition shadow-xs inline-flex items-center gap-2 cursor-pointer"
              >
                <Search className="w-4 h-4" />
                <span>Try Local Search Free</span>
              </a>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {features.map((feat) => (
            <div
              key={feat.slug}
              className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-600 transition flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                    {getIcon(feat.slug)}
                  </div>
                  <h2 className="text-base font-bold text-slate-900 dark:text-white">
                    <a
                      href={`/features/${feat.slug}`}
                      onClick={(e) => handleLinkClick(e, `/features/${feat.slug}`)}
                      className="hover:text-indigo-600 dark:hover:text-indigo-400 transition cursor-pointer"
                    >
                      {feat.name}
                    </a>
                  </h2>
                </div>

                <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
                  {feat.shortDescription}
                </p>

                <div className="mb-4">
                  <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider block mb-1.5">
                    Key Benefit:
                  </span>
                  <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1">
                    {feat.keyBenefits.slice(0, 2).map((b, i) => (
                      <li key={i} className="flex items-center gap-1.5">
                        <Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                <span className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">
                  Free & Pro Tiers
                </span>
                <a
                  href={`/features/${feat.slug}`}
                  onClick={(e) => handleLinkClick(e, `/features/${feat.slug}`)}
                  className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 flex items-center gap-1 cursor-pointer"
                >
                  <span>Explore Tool</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          ))}
        </div>

        <SeoRelatedLinks currentRoute="/features" onNavigate={onNavigate} />
      </main>
    </div>
  );
}
